/**
 * Authentication Service for EDX Red
 * Handles user authentication and related API requests
 */

import { post, get, put } from './apiClient';

// Auth API endpoints
const AUTH_ENDPOINTS = {
  REGISTER: '/api/auth/register',
  LOGIN: '/api/auth/login',
  LOGOUT: '/api/auth/logout',
  VERIFY_EMAIL: '/api/auth/verify',
  RESET_PASSWORD: '/api/auth/reset-password',
  CURRENT_USER: '/api/users/me',
  UPDATE_PROFILE: '/api/users/me',
  UPDATE_THEME: '/api/users/me/theme',
};

/**
 * AuthService handles all authentication-related API calls
 */
const AuthService = {
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise} - Promise that resolves to the API response
   */
  register: async (userData) => {
    return post(AUTH_ENDPOINTS.REGISTER, userData);
  },

  /**
   * Login user
   * @param {Object} credentials - User login credentials
   * @returns {Promise} - Promise that resolves to the API response with token
   */
  login: async (credentials) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Check specifically for suspended/banned status responses
        if (response.status === 403 && (data.status === 'suspended' || data.status === 'banned')) {
          console.log('Account is ' + data.status, data); // Debug log
          
          // Store status details in localStorage for the account status page
          localStorage.setItem('statusDetails', JSON.stringify({
            status: data.status,
            until: data.until,
            reason: data.reason,
            changedAt: data.changedAt
          }));
          
          // Set a special flag in localStorage to trigger the redirect
          localStorage.setItem('accountNeedsStatusPage', 'true');
          
          // Throw an error with a clear message
          throw new Error(`Your account has been ${data.status}. Please check the status page for details.`);
        }
        
        // For other errors
        throw new Error(data.error || 'Login failed');
      }
      
      // Clear any previous status flags on successful login
      localStorage.removeItem('accountNeedsStatusPage');
      return data;
    } catch (error) {
      console.error('AuthService login error:', error); // Debug log
      throw error;
    }
  },

  /**
   * Logout user
   * @returns {Promise} - Promise that resolves to the API response
   */
  logout: async () => {
    return post(AUTH_ENDPOINTS.LOGOUT);
  },

  /**
   * Verify email with token
   * @param {string} token - Email verification token
   * @returns {Promise} - Promise that resolves to the API response
   */
  verifyEmail: async (token) => {
    return get(`${AUTH_ENDPOINTS.VERIFY_EMAIL}/${token}`);
  },

  /**
   * Request password reset
   * @param {Object} data - Contains email for password reset
   * @returns {Promise} - Promise that resolves to the API response
   */
  requestPasswordReset: async (data) => {
    return post(AUTH_ENDPOINTS.RESET_PASSWORD, data);
  },

  /**
   * Reset password with token
   * @param {string} token - Password reset token
   * @param {Object} data - Contains new password
   * @returns {Promise} - Promise that resolves to the API response
   */
  resetPassword: async (token, data) => {
    return post(`${AUTH_ENDPOINTS.RESET_PASSWORD}/${token}`, data);
  },

  /**
   * Get current user profile
   * @returns {Promise} - Promise that resolves to the user data
   */
  getCurrentUser: async () => {
    return get(AUTH_ENDPOINTS.CURRENT_USER);
  },

  /**
   * Update user profile
   * @param {Object} userData - Updated user data
   * @returns {Promise} - Promise that resolves to the updated user data
   */
  updateProfile: async (userData) => {
    return put(AUTH_ENDPOINTS.UPDATE_PROFILE, userData);
  },

  /**
   * Update user theme preference
   * @param {string} themePreference - Theme preference
   * @returns {Promise} - Promise that resolves to the API response
   */
  updateTheme: async (themePreference) => {
    return put(AUTH_ENDPOINTS.UPDATE_THEME, { themePreference });
  },
};

export default AuthService;